import React from "react";
import ChoixChips from "../ChoixChips/ChoixChips";
import { toInputDate } from "./listHelpers";

// ============================================================
// Rend un champ de filtre à partir de sa définition :
// { name, label, type, options, dataIndex, dataLabel, optionLabel,
//   firstOptionLabel, placeholder, hint, size, shortLabel, presets }
// Types :
// - text (défaut), select, date, checkbox : champs simples ;
// - segmented : choix exclusif court (« Tous / Oui / Non ») en options
//   jointes, un clic au lieu d'ouvrir une liste ;
// - multi : cases à cocher en ligne (valeur = tableau) ;
// - dateRange : une période sur deux clés (`from`, `to`), un seul champ ;
// - custom : `render({ value, values, onChange, onSet })` pour un champ propre
//   à un écran (autocomplétion, sélecteur de jour).
// La largeur suit le type (un champ date n'a pas besoin de 400 px) ; `size`
// (sm | md | lg | xl) l'ajuste. `variant="barre"` rend le champ compact, son
// libellé dans le cadre, pour la barre de recherche (filtres principaux).
// `onChange` reçoit un événement (DOM ou { target: { name, value, type } }) ;
// `onSet(name, value)` pose une valeur directement (vider, raccourci, période,
// choix multiple). `values` : toutes les valeurs (une période lit deux clés).
// ============================================================

const TAILLE_PAR_DEFAUT = { text: "lg", select: "md" };

const texte = (value) => (value === undefined || value === null ? "" : value);
const slug = (value) => String(value).replace(/[^a-zA-Z0-9_-]/g, "-");

function classes(definition, type, variant) {
  const taille = definition.size || TAILLE_PAR_DEFAUT[type];
  return [
    "filter-field",
    `filter-field--${type}`,
    taille ? `filter-field--${taille}` : "",
    variant === "barre" ? "filter-field--barre" : "",
    definition.className || "",
  ]
    .filter(Boolean)
    .join(" ");
}

const libelleCourt = (d) => d.shortLabel || d.chipLabel || d.label;

function optionsDe(d) {
  const index = d.dataIndex || "id";
  const label = d.dataLabel || "name";
  return (d.options || []).map((o) => ({
    valeur: o[index],
    libelle: d.optionLabel ? d.optionLabel(o) : o[label],
    disabled: !!(o.isDisabled || o.disabled),
  }));
}

const Aide = ({ id, children }) =>
  children ? (
    <small id={id} className="filter-field__aide">
      {children}
    </small>
  ) : null;

const BoutonVider = ({ label, onClick, disabled }) => (
  <button
    type="button"
    className="filter-field__vider"
    onClick={onClick}
    disabled={disabled}
    aria-label={`Vider « ${label} »`}
    title="Vider"
  >
    <i className="fa fa-times" aria-hidden="true" />
  </button>
);

const Raccourcis = ({ presets = [], onChoisir }) =>
  presets.map((preset) => (
    <button
      key={preset.label}
      type="button"
      className="filter-field__raccourci"
      onClick={() => onChoisir(preset.getValue())}
    >
      {preset.label}
    </button>
  ));

function ChampTexte({ d, value, onChange, variant }) {
  const aideId = d.hint ? `${d.name}-aide` : undefined;
  return (
    <div className={classes(d, "text", variant)}>
      <label htmlFor={d.name}>{variant === "barre" ? libelleCourt(d) : d.label}</label>
      <input
        id={d.name}
        name={d.name}
        type={d.inputType || "text"}
        className="form-control"
        value={texte(value)}
        placeholder={d.placeholder || ""}
        onChange={onChange}
        autoComplete="off"
        inputMode={d.inputMode}
        aria-describedby={aideId}
      />
      <Aide id={aideId}>{d.hint}</Aide>
    </div>
  );
}

function ChampSelect({ d, value, onChange, variant }) {
  return (
    <div className={classes(d, "select", variant)}>
      <label htmlFor={d.name} className={variant === "barre" ? "filter-field__prefixe" : undefined}>
        {variant === "barre" ? libelleCourt(d) : d.label}
      </label>
      <select
        id={d.name}
        name={d.name}
        className="form-control"
        value={texte(value)}
        onChange={onChange}
      >
        {d.firstOptionLabel !== null && <option value="">{d.firstOptionLabel || "Tous"}</option>}
        {optionsDe(d).map((o) => (
          <option key={`${d.name}-${o.valeur}`} value={o.valeur} disabled={o.disabled}>
            {o.libelle}
          </option>
        ))}
      </select>
    </div>
  );
}

// Choix exclusif court : l'option vide (« Tous ») d'abord, puis les options.
// La valeur remonte en chaîne, comme celle d'un select.
function ChampSegmente({ d, value, onChange, variant }) {
  const index = d.dataIndex || "id";
  const label = d.dataLabel || "name";
  const options = [
    ...(d.firstOptionLabel === null ? [] : [{ [index]: "", [label]: d.firstOptionLabel || "Tous" }]),
    ...(d.options || []),
  ];
  const barre = variant === "barre";
  return (
    <div className={classes(d, "segmented", variant)}>
      {barre && (
        <span className="filter-field__prefixe" aria-hidden="true">
          {libelleCourt(d)}
        </span>
      )}
      <ChoixChips
        name={d.name}
        legend={d.label}
        legendSrOnly={barre}
        options={options}
        dataIndex={index}
        dataLabel={label}
        value={texte(value)}
        variant="segmente"
        onChange={(valeur) => onChange({ target: { name: d.name, value: String(valeur), type: "radio" } })}
      />
    </div>
  );
}

// Choix multiple : cases à cocher en ligne ; la valeur est un tableau des
// valeurs d'options cochées (valeurs d'origine, pas les chaînes du DOM).
function ChampMultiple({ d, value, onSet, variant }) {
  const liste = Array.isArray(value) ? value : [];
  const coche = (v) => liste.map(String).includes(String(v));
  return (
    <fieldset className={classes(d, "multi", variant)}>
      <legend className="filter-field__legende">{d.label}</legend>
      <div className="filter-field__cases">
        {optionsDe(d).map((o) => {
          const id = `${d.name}-${slug(o.valeur)}`;
          return (
            <div className="custom-control custom-checkbox" key={id}>
              <input
                type="checkbox"
                className="custom-control-input"
                id={id}
                name={d.name}
                value={o.valeur}
                checked={coche(o.valeur)}
                disabled={o.disabled}
                onChange={(e) =>
                  onSet(
                    d.name,
                    e.target.checked
                      ? [...liste, o.valeur]
                      : liste.filter((v) => String(v) !== String(o.valeur))
                  )
                }
              />
              <label className="custom-control-label" htmlFor={id}>
                {o.libelle}
              </label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

// Interrupteur : même balisage que Control type="checkbox" (identifiant
// `customSwitch<name>` conservé), avec l'aide reliée au champ.
function ChampCase({ d, value, onChange, variant }) {
  const id = `customSwitch${d.name}`;
  const aideId = d.hint ? `${d.name}-aide` : undefined;
  return (
    <div className={classes(d, "checkbox", variant)}>
      <div className="custom-control custom-switch">
        <input
          type="checkbox"
          className="custom-control-input"
          id={id}
          name={d.name}
          checked={!!value}
          onChange={onChange}
          aria-describedby={aideId}
        />
        <label className="custom-control-label" htmlFor={id}>
          {d.label}
        </label>
      </div>
      <Aide id={aideId}>{d.hint}</Aide>
    </div>
  );
}

function ChampDate({ d, value, onChange, onSet, variant }) {
  return (
    <div className={classes(d, "date", variant)}>
      <label htmlFor={d.name}>{d.label}</label>
      <div className="filter-field__ligne">
        <input
          id={d.name}
          name={d.name}
          type="date"
          className="form-control"
          value={toInputDate(value)}
          onChange={onChange}
        />
        <BoutonVider label={d.label} onClick={() => onSet(d.name, "")} disabled={!value} />
        <Raccourcis presets={d.presets} onChoisir={(v) => onSet(d.name, v)} />
      </div>
    </div>
  );
}

// Période : un libellé, deux dates (« du … au … »), une croix pour tout vider.
// Chaque date garde son libellé complet pour les lecteurs d'écran.
function ChampPeriode({ d, values = {}, onChange, onSet, variant }) {
  const du = toInputDate(values[d.from]);
  const au = toInputDate(values[d.to]);
  const inversee = !!(du && au && du > au);
  const alerteId = `${d.name}-alerte`;
  return (
    <fieldset className={classes(d, "dateRange", variant)}>
      <legend className="filter-field__legende">{d.label}</legend>
      <div className="filter-field__ligne">
        <span className="filter-field__borne">
          <span className="filter-field__lien" aria-hidden="true">
            du
          </span>
          <input
            id={d.from}
            name={d.from}
            type="date"
            className="form-control"
            value={du}
            onChange={onChange}
            aria-label={d.fromLabel || `${d.label} : à partir du`}
            aria-invalid={inversee || undefined}
            aria-describedby={inversee ? alerteId : undefined}
          />
        </span>
        <span className="filter-field__borne">
          <span className="filter-field__lien" aria-hidden="true">
            au
          </span>
          <input
            id={d.to}
            name={d.to}
            type="date"
            className="form-control"
            value={au}
            onChange={onChange}
            aria-label={d.toLabel || `${d.label} : jusqu'au`}
            aria-invalid={inversee || undefined}
            aria-describedby={inversee ? alerteId : undefined}
          />
        </span>
        <BoutonVider
          label={d.label}
          onClick={() => {
            onSet(d.from, "");
            onSet(d.to, "");
          }}
          disabled={!du && !au}
        />
        <Raccourcis
          presets={d.presets}
          onChoisir={(periode) => {
            onSet(d.from, (periode && periode.from) || "");
            onSet(d.to, (periode && periode.to) || "");
          }}
        />
      </div>
      {inversee && (
        <p id={alerteId} className="filter-field__alerte" role="status">
          La date de début est postérieure à la date de fin.
        </p>
      )}
    </fieldset>
  );
}

const FilterField = ({ definition: d, value, values, onChange, onSet, variant = "panneau" }) => {
  const props = { d, value, values, onChange, onSet, variant };
  switch (d.type) {
    case "select":
      return <ChampSelect {...props} />;
    case "segmented":
      return <ChampSegmente {...props} />;
    case "multi":
      return <ChampMultiple {...props} />;
    case "checkbox":
      return <ChampCase {...props} />;
    case "date":
      return <ChampDate {...props} />;
    case "dateRange":
      return <ChampPeriode {...props} />;
    case "custom":
      return (
        <div className={classes(d, "custom", variant)}>
          {d.render({ value, values, onChange, onSet })}
        </div>
      );
    default:
      return <ChampTexte {...props} />;
  }
};

export default FilterField;
